import { createContext, useContext, useEffect, ReactNode, useCallback } from 'react';
import { usePreferencesStore, selectResolvedTheme } from '@/shared/stores/preferences.store';
import { ThemeMode } from '@/features/auth/domain/value-objects/UserPreferences';
import { UpdateUserPreferencesUseCase } from '@/features/auth/application/use-cases/UpdateUserPreferencesUseCase';
import { SupabaseUserPreferencesRepository } from '@/features/auth/infrastructure/repositories/SupabaseUserPreferencesRepository';
import { useAuth } from '@/features/auth/presentation/contexts/AuthContext';

interface ThemeContextValue {
  theme: ThemeMode;
  resolvedTheme: 'light' | 'dark';
  setTheme: (theme: ThemeMode) => Promise<void>;
  isSyncing: boolean;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const { user } = useAuth();
  const store = usePreferencesStore();
  const resolvedTheme = selectResolvedTheme(store);

  // Apply theme class to document
  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(resolvedTheme);
  }, [resolvedTheme]);

  // Listen for system theme changes when using 'system' preference
  useEffect(() => {
    if (store.theme !== 'system') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      const newResolvedTheme = mediaQuery.matches ? 'dark' : 'light';
      document.documentElement.classList.remove('light', 'dark');
      document.documentElement.classList.add(newResolvedTheme);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [store.theme]);

  // Sync theme from user preferences ONLY on login (when userId changes)
  // Do NOT override local changes - the local store is the source of truth
  // and syncs to the database in the background
  useEffect(() => {
    if (user?.preferences?.theme && user.id !== store.userId) {
      // On login: set user ID and theme from database
      store.setUserId(user.id);
      store.setTheme(user.preferences.theme);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.preferences?.theme, store.userId]);

  // Sync LMS preferences from user ONLY on login (when userId changes)
  // Do NOT override local changes - the local store is the source of truth
  useEffect(() => {
    if (user?.preferences?.lms && user.id !== store.userId) {
      // On login: set LMS preferences from database
      store.setLMSPreferences(user.preferences.lms);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.preferences?.lms, store.userId]);

  // Reset store on logout
  useEffect(() => {
    if (!user && store.userId) {
      store.reset();
    }
  }, [user, store]);

  const setTheme = useCallback(async (theme: ThemeMode) => {
    // Optimistic update - apply immediately
    store.setTheme(theme);

    // Sync to database if user is logged in
    if (user?.id) {
      store.setSyncing(true);
      store.setSyncError(null);

      try {
        const repository = new SupabaseUserPreferencesRepository();
        const useCase = new UpdateUserPreferencesUseCase(repository);
        await useCase.execute({ userId: user.id, theme });
        store.setLastSynced(Date.now());
      } catch (error) {
        console.error('Failed to sync theme preference:', error);
        store.setSyncError(error instanceof Error ? error.message : 'Sync failed');
        // Theme is already applied locally, so user experience is not affected
      } finally {
        store.setSyncing(false);
      }
    }
  }, [user, store]);

  const value: ThemeContextValue = {
    theme: store.theme,
    resolvedTheme,
    setTheme,
    isSyncing: store.isSyncing,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
